import {
  TILE_SIZE, FOOT_HALF_W, FOOT_HALF_H,
} from './events';

/**
 * Working out how to walk somewhere.
 *
 * WHY THIS IS ON THE CLIENT AT ALL. The server's rule is that clients submit
 * intent — four held-direction booleans — and never a position. Click-to-move
 * keeps that intact by turning a click into a destination HERE and then
 * producing exactly those four booleans each frame. The server needs no changes
 * and a forged position still has nowhere to land.
 *
 * THE COST, STATED PLAINLY: this is a second walkability model, and the first
 * is `server/game/collision.js`. It is small and built from the same shared
 * constants, but if the foot box ever changes, this is the other place that has
 * to know.
 *
 * WHY THE NODE GRID IS ASYMMETRIC, which is the whole design of this file and
 * was found by the tests rather than by reasoning.
 *
 * The foot box is 18 WIDE and 10 TALL against 16-wide tiles, so the two axes
 * are not the same problem at all:
 *
 *   HORIZONTALLY the box is wider than a tile. A 2-tile doorway is passable,
 *   but ONLY off-centre — between walls the box fits at x = 60..68, and at
 *   neither tile centre (56 or 72). Pathing on tile centres would refuse a
 *   doorway that commit 402d6d1 says has 7px of clearance. So X needs half-tile
 *   nodes. A 1-tile corridor still fits nowhere, which is exactly the rule that
 *   commit exists to protect.
 *
 *   VERTICALLY the box is SHORTER than a tile, so a tile centre always fits
 *   inside an open row — and is the only thing that does in a one-tile-tall
 *   corridor. Half-tile nodes at y = 52 and 60 both straddle the row boundary
 *   and get rejected, which made a perfectly walkable corridor unroutable.
 *
 * So: X every half tile, Y every tile. Cheaper than a uniform fine grid, and
 * correct in both axes for the reason each axis actually has.
 */

/** X every half tile — the box is wider than a tile. */
export const NODE_X = TILE_SIZE / 2;
/** Y every tile — the box is shorter than one, so centres always fit. */
export const NODE_Y = TILE_SIZE;

/** How far from the click to look for somewhere standable, in nodes. */
const SNAP_RADIUS = 4;

/** Four-way. Diagonals are absent: the server normalises diagonal speed, and a
 *  diagonal step past a corner can clip geometry a two-step L never touches. */
const NEIGHBOURS = [[1, 0], [-1, 0], [0, 1], [0, -1]];

/** Matches the EPSILON in server/game/collision.js, so a box that exactly fills
 *  a tile is judged the same on both sides. */
const EPSILON = 0.001;

const key = (nx, ny) => `${nx},${ny}`;

/** World position of a node, in the foot space collision and routes both use. */
export const nodePoint = (nx, ny) => ({
  x: nx * NODE_X + NODE_X / 2,
  y: ny * NODE_Y + NODE_Y / 2,
});

/** Which node a foot-space point falls in. */
export const nodeOf = (footX, footY) => ({
  nx: Math.floor(footX / NODE_X),
  ny: Math.floor(footY / NODE_Y),
});

/**
 * Could the character stand with its feet here?
 *
 * Mirrors `boxHitsSolid` in server/game/collision.js — same box, same shared
 * constants, same out-of-bounds-counts-as-solid rule.
 */
export function canStandAtPoint(grid, cx, cy) {
  const { walls, width, height } = grid;

  const left = Math.floor((cx - FOOT_HALF_W) / TILE_SIZE);
  const right = Math.floor((cx + FOOT_HALF_W - EPSILON) / TILE_SIZE);
  const top = Math.floor((cy - FOOT_HALF_H) / TILE_SIZE);
  const bottom = Math.floor((cy + FOOT_HALF_H - EPSILON) / TILE_SIZE);

  for (let ty = top; ty <= bottom; ty += 1) {
    for (let tx = left; tx <= right; tx += 1) {
      if (tx < 0 || ty < 0 || tx >= width || ty >= height) return false;
      if (walls[ty * width + tx] !== 0) return false;
    }
  }
  return true;
}

/** The same question, asked about a node. */
export function canStandAt(grid, nx, ny) {
  const { x, y } = nodePoint(nx, ny);
  return canStandAtPoint(grid, x, y);
}

/**
 * The nearest node you could actually stand on, spiralling out from a click.
 *
 * Clicking a wall should walk you up to it rather than doing nothing — "nothing
 * happened" reads as a broken click.
 */
function nearestStandable(grid, nx, ny) {
  if (canStandAt(grid, nx, ny)) return { nx, ny };

  for (let r = 1; r <= SNAP_RADIUS; r += 1) {
    for (let dy = -r; dy <= r; dy += 1) {
      for (let dx = -r; dx <= r; dx += 1) {
        if (Math.abs(dx) !== r && Math.abs(dy) !== r) continue; // eslint-disable-line no-continue
        if (canStandAt(grid, nx + dx, ny + dy)) return { nx: nx + dx, ny: ny + dy };
      }
    }
  }
  return null;
}

/**
 * A route from one foot-space point to another, as foot-space waypoints.
 *
 * A* with a Manhattan heuristic, admissible for four-way movement and so still
 * a shortest path. Runs once per click, not per frame.
 *
 * @returns {{x: number, y: number}[]} empty when already there or unreachable.
 *   The caller treats empty as "do nothing", so a click into solid rock stops
 *   rather than walking hopefully into a wall.
 */
export function findPath(grid, from, to) {
  if (!grid?.walls || !grid.width || !grid.height) return [];

  const start = nodeOf(from.x, from.y);
  const clicked = nodeOf(to.x, to.y);

  const goal = nearestStandable(grid, clicked.nx, clicked.ny);
  if (!goal) return [];
  if (goal.nx === start.nx && goal.ny === start.ny) return [];

  // Searching from where the player actually is, even if this model calls it
  // unstandable — the server is the authority and may have nudged them, and
  // refusing to move would be worse than a slightly odd first step.
  const open = [{ nx: start.nx, ny: start.ny, f: 0 }];
  const cameFrom = new Map();
  const cost = new Map([[key(start.nx, start.ny), 0]]);
  const heuristic = (nx, ny) => Math.abs(nx - goal.nx) + Math.abs(ny - goal.ny);

  while (open.length > 0) {
    let bestAt = 0;
    for (let i = 1; i < open.length; i += 1) if (open[i].f < open[bestAt].f) bestAt = i;
    const current = open.splice(bestAt, 1)[0];

    if (current.nx === goal.nx && current.ny === goal.ny) {
      const path = [];
      let at = key(current.nx, current.ny);
      let node = { nx: current.nx, ny: current.ny };

      while (at) {
        path.unshift(nodePoint(node.nx, node.ny));
        const previous = cameFrom.get(at);
        if (!previous) break;
        node = previous;
        at = key(previous.nx, previous.ny);
      }

      // Drop the node already stood in; walking to where you are reads as a
      // stutter on the first step.
      return path.slice(1);
    }

    const soFar = cost.get(key(current.nx, current.ny));

    NEIGHBOURS.forEach(([dx, dy]) => {
      const nx = current.nx + dx;
      const ny = current.ny + dy;
      if (!canStandAt(grid, nx, ny)) return;

      const next = soFar + 1;
      const id = key(nx, ny);
      if (cost.has(id) && cost.get(id) <= next) return;

      cost.set(id, next);
      cameFrom.set(id, { nx: current.nx, ny: current.ny });
      open.push({ nx, ny, f: next + heuristic(nx, ny) });
    });
  }

  return []; // unreachable — walled off, or nothing standable near the click
}

/**
 * How close counts as arrived, and how far off-axis before a key is held.
 *
 * ONE CONSTANT FOR BOTH, deliberately — see `reached` for what happened when
 * they were separate. It also has to exceed half a tick of movement (110/20 =
 * 5.5, so 2.75) or the character can straddle a waypoint and oscillate.
 */
export const ARRIVE = 3;

/**
 * Which keys to hold to get from here to the next waypoint.
 *
 * The same shape AND the same key order as `Input.snapshot()`, because the
 * engine only emits when the serialised value changes — a different key order
 * would serialise differently for an identical intent and cause spurious
 * packets on every switch between walking and steering.
 *
 * The deadband matters for the same reason it does in npc.js — without it,
 * being a unit off-axis holds a diagonal, and the server normalises diagonals,
 * so the character approaches at an angle and jitters on arrival.
 */
export function steer(fromFoot, waypoint, arrive = ARRIVE) {
  const dx = waypoint.x - fromFoot.x;
  const dy = waypoint.y - fromFoot.y;

  return {
    up: dy < -arrive,
    down: dy > arrive,
    left: dx < -arrive,
    right: dx > arrive,
  };
}

/**
 * Has the character reached this waypoint?
 *
 * PER-AXIS, ON THE SAME THRESHOLD `steer` USES, and that is the whole point of
 * this function's shape. It was Euclidean, and `steer` was per-axis, so they
 * disagreed: at an offset of 2.5 across and 2.5 down, steer pressed nothing
 * (both within the deadband) while reached said no (distance 3.54 > 3). The
 * character stopped, the waypoint never advanced, and the click looked ignored.
 * That dead zone was 12% of the area around every waypoint — and Y nodes sit at
 * tile centres while feet stop anywhere, so an ordinary sideways walk landed in
 * it constantly.
 *
 * Written this way, "reached" is the exact negation of "steer presses
 * something", so every position is either walking or arrived. The deadlock is
 * impossible by construction rather than by picking luckier numbers, and
 * `pathfind.test.js` sweeps the area to keep it that way.
 */
export const reached = (fromFoot, waypoint, arrive = ARRIVE) => (
  Math.abs(waypoint.x - fromFoot.x) <= arrive
  && Math.abs(waypoint.y - fromFoot.y) <= arrive
);

export default findPath;
